/* import __COLOCATED_TEMPLATE__ from './learn.hbs'; */
var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4;
function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'transform-class-properties is enabled and runs after the decorators transform.'); }
import Component from '@glimmer/component';
import { Severity } from '@sentry/browser';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { LEARN_CLICK_EVENT } from '@banking/ui/utils/launchpad/event-catalog';
import { linkClickEvent } from '@dashboard/junk-drawer/utils/eventstream';
import { Country } from '@square/dashboard-protos/models/squareup/common/countries';
import { Action, Target, getEvent } from '@banking/ui/utils/launchpad-cdp-events';
const US_AND_CANADA = new Set([Country.CA, Country.US]);
const US = new Set([Country.US]);
const CAN_SEE_LOANS_FEATURE_FLAG_STRING = 'canSeeLoans';
/**
 * NOTE(steckel): The `supportedCountries` field is naively populated.
 *
 * As of writing this comment the only intentionally supported countries who
 * *should* see this component are U.S. and Canada therefore that's the set of
 * countries *tested* for.
 *
 * To check the supported countries for a given article visit the URL and
 * notice the URL redirect will inject your current locale (e.g.
 * https://squareup.com/townsquare/smart-stores-that-take-apple-pay will
 * redirect to
 * https://squareup.com/us/en/townsquare/smart-stores-that-take-apple-pay).
 * Change the country code in the URL and check to see if you get a 404. Good
 * enough "for now."
 */
const LESSONS = [{
  headline: 'banking.launchpad.learn.loan',
  link: 'https://squareup.com/us/en/townsquare/a-guide-to-business-loans',
  imageUrl: '/@banking/ui/assets/images/launchpad/guide-to-loans.png',
  supportedCountries: US_AND_CANADA,
  supportedFor: CAN_SEE_LOANS_FEATURE_FLAG_STRING
}, {
  headline: 'banking.launchpad.learn.contactless',
  link: 'https://squareup.com/us/en/townsquare/what-is-a-contactless-payment',
  imageUrl: '/@banking/ui/assets/images/launchpad/contactless-payment.png',
  supportedCountries: new Set([Country.CA, Country.US, Country.GB, Country.AU, Country.IE])
}, {
  headline: 'banking.launchpad.learn.qrCode',
  link: 'https://squareup.com/us/en/townsquare/qr-code-ordering-system-ticket-size',
  imageUrl: '/@banking/ui/assets/images/launchpad/qr-code-ordering.png',
  supportedCountries: new Set([Country.CA, Country.US, Country.AU])
}, {
  headline: 'banking.launchpad.learn.credit',
  link: 'https://squareup.com/us/en/townsquare/what-is-a-business-line-of-credit',
  imageUrl: '/@banking/ui/assets/images/launchpad/business-line-of-credit.png',
  supportedCountries: US_AND_CANADA
}, {
  headline: 'banking.launchpad.learn.refinancing',
  link: 'https://squareup.com/us/en/townsquare/what-is-refinancing',
  imageUrl: '/@banking/ui/assets/images/launchpad/refinancing.png',
  supportedCountries: US_AND_CANADA
}, {
  headline: 'banking.launchpad.learn.inflation',
  link: 'https://squareup.com/us/en/townsquare/what-is-inflation',
  imageUrl: '/@banking/ui/assets/images/launchpad/what-is-inflation.png',
  supportedCountries: US
}, {
  headline: 'banking.launchpad.learn.solopreneurship',
  link: 'https://squareup.com/us/en/townsquare/tips-for-solopreneurs',
  imageUrl: '/@banking/ui/assets/images/launchpad/consider-solopreneurship.png',
  supportedCountries: US
}, {
  headline: 'banking.launchpad.learn.balanceSheet',
  link: 'https://squareup.com/us/en/townsquare/balance-sheet',
  imageUrl: '/@banking/ui/assets/images/launchpad/create-and-use-a-balance-sheet.png',
  supportedCountries: US
}, {
  headline: 'banking.launchpad.learn.applePay',
  link: 'https://squareup.com/townsquare/smart-stores-that-take-apple-pay',
  imageUrl: '/@banking/ui/assets/images/launchpad/apple-pay-retailers.png',
  supportedCountries: US
}, {
  headline: 'banking.launchpad.learn.bep',
  link: 'https://squareup.com/townsquare/how-to-calculate-break-even-point-analysis',
  imageUrl: '/@banking/ui/assets/images/launchpad/break-even-point.png',
  supportedCountries: US_AND_CANADA
}, {
  headline: 'banking.launchpad.learn.ccAuth',
  link: 'https://squareup.com/townsquare/credit-card-authorization-form-templates',
  imageUrl: '/@banking/ui/assets/images/launchpad/credit-card-auth.png',
  supportedCountries: US_AND_CANADA
}, {
  headline: 'banking.launchpad.learn.ach',
  link: 'https://squareup.com/townsquare/ach-payments',
  imageUrl: '/@banking/ui/assets/images/launchpad/ach-payments.png',
  supportedCountries: US
}, {
  headline: 'banking.launchpad.learn.acceptApplePay',
  link: 'https://squareup.com/townsquare/apple-pay-for-small-businesses-how-to-accept-it',
  imageUrl: '/@banking/ui/assets/images/launchpad/accept-apple-pay.png',
  supportedCountries: US_AND_CANADA
}, {
  headline: 'banking.launchpad.learn.profitable',
  link: 'https://squareup.com/townsquare/what-is-a-profitable-business',
  imageUrl: '/@banking/ui/assets/images/launchpad/profitable.png',
  supportedCountries: US
}, {
  headline: 'banking.launchpad.learn.omnichannel',
  link: 'https://squareup.com/us/en/townsquare/payment-decision-tree',
  imageUrl: '/@banking/ui/assets/images/launchpad/omnichannel.png',
  supportedCountries: US
}, {
  headline: 'banking.launchpad.learn.bankStatement',
  link: 'https://squareup.com/us/en/townsquare/how-to-use-bank-statements',
  imageUrl: '/@banking/ui/assets/images/launchpad/bank-statement.png',
  supportedCountries: US_AND_CANADA
}, {
  headline: 'banking.launchpad.learn.separatePersonal',
  link: 'https://squareup.com/us/en/the-bottom-line/podcasts/paying-it-forward/separate-personal-and-business-finances',
  imageUrl: '/@banking/ui/assets/images/launchpad/separate-personal.png',
  supportedCountries: US_AND_CANADA
}, {
  headline: 'banking.launchpad.learn.profitLoss',
  link: 'https://squareup.com/us/en/townsquare/paying-it-forward-what-is-profit-and-loss-statement',
  imageUrl: '/@banking/ui/assets/images/launchpad/profit-and-loss.png',
  supportedCountries: US_AND_CANADA
}];
let Learn = (_class = class Learn extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "userInfo", _descriptor, this);
    _initializerDefineProperty(this, "events", _descriptor2, this);
    _initializerDefineProperty(this, "intl", _descriptor3, this);
    _initializerDefineProperty(this, "sentry", _descriptor4, this);
  }
  /**
   * Returns only lessons that are supported for:
   *   - countries within the supportedCountries attribute on the lesson
   *   - userInfo property within the supportedFor attribute on the lesson
   */
  get supportedLessonsByCountryAndFeature() {
    const country = Country[this.userInfo.countryCode];
    const supportedLessons = [];
    LESSONS.forEach(lesson => {
      if (lesson.supportedCountries.has(country)) {
        if (lesson.supportedFor != null && eval(`this.userInfo.${lesson.supportedFor}`) // eslint-disable-line no-eval
        ) {
          supportedLessons.push(lesson);
        } else if (lesson.supportedFor == null) {
          supportedLessons.push(lesson);
        }
      }
    });
    return supportedLessons;
  }

  /**
   * Gets a random lesson from LESSONS constant.
   * When no lesson is available for the current user's country fallback to
   * U.S.-based lessons and log warning.
   */
  get randomLesson() {
    const country = Country[this.userInfo.countryCode];
    const supportedLessons = this.supportedLessonsByCountryAndFeature;
    const lessons = supportedLessons.length > 0 ? supportedLessons : LESSONS;
    if (supportedLessons.length === 0) {
      this.sentry.captureMessage(`No lessons were found for Country: ${Country[country]}.`, Severity.Warning);
    }
    const lesson = lessons[Math.floor(Math.random() * lessons.length)];
    return {
      ...lesson,
      headline: this.intl.t(lesson.headline),
      title: lesson.title == null ? undefined : this.intl.t(lesson.title)
    };
  }
  trackLearnClickEvent(lesson) {
    this.events.track({
      kind: 'EVENTSTREAM',
      event: linkClickEvent(LEARN_CLICK_EVENT)
    });
    this.events.track({
      kind: 'CDP',
      event: getEvent(Action.Click, {
        target: Target.Learn,
        content: lesson.title || lesson.headline
      })
    });
  }
}, (_descriptor = _applyDecoratedDescriptor(_class.prototype, "userInfo", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "events", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "intl", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "sentry", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "trackLearnClickEvent", [action], Object.getOwnPropertyDescriptor(_class.prototype, "trackLearnClickEvent"), _class.prototype)), _class);
export { Learn as default };